import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Switch,
    Typography,
    Divider,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDashboardPreferences, TileSize } from '../context/DashboardPreferencesContext';

function DashboardSettings({ open, onClose, availableZones }) {
    const { t } = useTranslation();
    const {
        tileSize,
        setTileSize,
        isZoneHidden,
        toggleZoneVisibility,
        showAllZones,
        resetPreferences,
    } = useDashboardPreferences();

    const handleTileSizeChange = (event) => {
        setTileSize(event.target.value);
    };

    const handleShowAll = () => {
        showAllZones();
    };

    const handleReset = () => {
        resetPreferences();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('common.dashboardSettings')}</DialogTitle>
            <DialogContent>
                {/* Tile Size Selection */}
                <Box sx={{ mb: 3, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id="tile-size-label">{t('common.tileSize')}</InputLabel>
                        <Select
                            labelId="tile-size-label"
                            value={tileSize}
                            label={t('common.tileSize')}
                            onChange={handleTileSizeChange}
                        >
                            <MenuItem value={TileSize.COMPACT}>{t('common.compact')}</MenuItem>
                            <MenuItem value={TileSize.NORMAL}>{t('common.normal')}</MenuItem>
                            <MenuItem value={TileSize.EXPANDED}>{t('common.expanded')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Zone Visibility */}
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t('common.visibleZones')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('common.visibleZonesDescription')}
                </Typography>

                <List dense>
                    {availableZones.map((zone) => {
                        const zoneKey = zone || 'null';
                        const zoneName = zone || t('common.unassignedZone');
                        const isHidden = isZoneHidden(zone);

                        return (
                            <ListItem key={zoneKey}>
                                <ListItemText primary={zoneName} />
                                <ListItemSecondaryAction>
                                    <Switch
                                        edge="end"
                                        checked={!isHidden}
                                        onChange={() => toggleZoneVisibility(zone)}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })}
                </List>

                {availableZones.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                        {t('common.noZonesAvailable')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset} color="warning">
                    {t('common.resetToDefault')}
                </Button>
                <Button onClick={handleShowAll}>
                    {t('common.showAll')}
                </Button>
                <Button onClick={onClose} variant="contained">
                    {t('common.done')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DashboardSettings;
